import {
  collection,
  addDoc,
  setDoc,
  getDocs,
  query,
  where,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  increment,
  deleteField,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';

export const MUSIC_CATEGORIES = [
  'ambient',
  'lofi',
  'synthwave',
  'chillwave',
  'downtempo',
  'idm',
  'futurebass',
] as const;

export type MusicCategory = (typeof MUSIC_CATEGORIES)[number];

export interface MusicTrack {
  id: string;
  title: string;
  artist: string;
  album: string;
  albumArtist?: string;
  category: MusicCategory;
  storagePath: string;
  coverPath?: string;
  duration: number;
  bitrate?: number;
  sampleRate?: number;
  numberOfChannels?: number;
  codec?: string;
  year?: number;
  genre?: string;
  comment?: string;
  bpm?: number;
  trackNo?: number;
  diskNo?: number;
  composer?: string;
  lyricist?: string;
  isrc?: string;
  originalFilename?: string;
  fileSize?: number;
  playCount: number;
  lastPlayedAt?: string;
  excluded?: boolean; // Исключен из плейлиста (не показывается в /api/tracks)
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface CreateMusicTrack {
  title: string;
  artist: string;
  album: string;
  albumArtist?: string;
  category: MusicCategory;
  storagePath: string;
  coverPath?: string;
  duration: number;
  bitrate?: number;
  sampleRate?: number;
  numberOfChannels?: number;
  codec?: string;
  year?: number;
  genre?: string;
  comment?: string;
  bpm?: number;
  trackNo?: number;
  diskNo?: number;
  composer?: string;
  lyricist?: string;
  isrc?: string;
  originalFilename?: string;
  fileSize?: number;
  excluded?: boolean;
}

export async function getMusicTracks(category?: MusicCategory): Promise<MusicTrack[]> {
  const coll = collection(db, 'music_tracks');
  const q = category
    ? query(coll, where('category', '==', category), orderBy('createdAt', 'desc'))
    : query(coll, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      title: data.title ?? '',
      artist: data.artist ?? '',
      album: data.album ?? '',
      albumArtist: data.albumArtist,
      category: data.category ?? 'ambient',
      storagePath: data.storagePath ?? '',
      coverPath: data.coverPath,
      duration: data.duration ?? 0,
      bitrate: data.bitrate,
      sampleRate: data.sampleRate,
      numberOfChannels: data.numberOfChannels,
      codec: data.codec,
      year: data.year,
      genre: data.genre,
      comment: data.comment,
      bpm: data.bpm,
      trackNo: data.trackNo,
      diskNo: data.diskNo,
      composer: data.composer,
      lyricist: data.lyricist,
      isrc: data.isrc,
      originalFilename: data.originalFilename,
      fileSize: data.fileSize,
      playCount: data.playCount ?? 0,
      lastPlayedAt: data.lastPlayedAt,
      excluded: data.excluded ?? false,
      createdAt: data.createdAt ?? '',
      updatedAt: data.updatedAt ?? '',
      createdBy: data.createdBy ?? '',
    } as MusicTrack;
  });
}

function omitUndefined<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v !== undefined)
  ) as Record<string, unknown>;
}

/** Generates a new Firestore document ID for music_tracks (use before upload for metadata) */
export function generateTrackId(): string {
  return doc(collection(db, 'music_tracks')).id;
}

export async function addMusicTrack(
  data: CreateMusicTrack,
  userId: string,
  trackId?: string
): Promise<string> {
  const now = new Date().toISOString();
  const docData = omitUndefined({
    ...data,
    playCount: 0,
    createdAt: now,
    updatedAt: now,
    createdBy: userId,
  });
  if (trackId) {
    await setDoc(doc(db, 'music_tracks', trackId), docData);
    return trackId;
  }
  const docRef = await addDoc(collection(db, 'music_tracks'), docData);
  return docRef.id;
}

export async function updateMusicTrack(
  trackId: string,
  data: Partial<CreateMusicTrack>
): Promise<void> {
  const docRef = doc(db, 'music_tracks', trackId);
  const docData = omitUndefined({
    ...data,
    updatedAt: new Date().toISOString(),
  }) as Record<string, string | number | boolean | null>;
  await updateDoc(docRef, docData);
}

export async function deleteMusicTrack(trackId: string): Promise<void> {
  await deleteDoc(doc(db, 'music_tracks', trackId));
}

async function deleteStorageFileIfExists(path: string): Promise<void> {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (err: unknown) {
    const code = (err as { code?: string })?.code;
    if (code !== 'storage/object-not-found') throw err;
  }
}

/** Удаляет трек из Firestore, MP3 и обложку из Storage */
export async function deleteMusicTrackFull(track: MusicTrack): Promise<void> {
  if (track.storagePath) {
    await deleteStorageFileIfExists(track.storagePath);
  }
  if (track.coverPath) {
    await deleteStorageFileIfExists(track.coverPath);
  }
  await deleteDoc(doc(db, 'music_tracks', track.id));
}

/** Reset playCount and lastPlayedAt for all tracks */
export async function resetAllPlayCounts(): Promise<number> {
  const coll = collection(db, 'music_tracks');
  const snapshot = await getDocs(query(coll, orderBy('createdAt', 'desc')));
  let count = 0;
  for (const d of snapshot.docs) {
    await updateDoc(doc(db, 'music_tracks', d.id), {
      playCount: 0,
      lastPlayedAt: deleteField(),
    });
    count++;
  }
  return count;
}

export async function incrementPlayCount(trackId: string): Promise<void> {
  const docRef = doc(db, 'music_tracks', trackId);
  await updateDoc(docRef, {
    playCount: increment(1),
    lastPlayedAt: new Date().toISOString(),
  });
}

export async function toggleTrackExcluded(trackId: string, excluded: boolean): Promise<void> {
  const docRef = doc(db, 'music_tracks', trackId);
  await updateDoc(docRef, {
    excluded: excluded,
    updatedAt: new Date().toISOString(),
  });
}

export async function uploadFile(
  path: string,
  file: File | Blob
): Promise<string> {
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

export async function getFileUrl(path: string): Promise<string> {
  const storageRef = ref(storage, path);
  return getDownloadURL(storageRef);
}
